import * as t from "io-ts";
import { IconName } from "@/lib/icons/icons";
import { UnitInputValue, UnitOptions } from "@/types/unit";
import { ClassPropertiesV } from "@/oxygen/classes/lib/type-properties";
import { ColorPickerType } from "@/types/colors";
// @ts-ignore - removing this line will cause a jest error
import { CodeInputLanguage } from "@/components/Reusable/CustomInputs/CodeInput/CodeInput.vue";

export const OxyNestedSelectorV = t.type({
  id: t.string,
  name: t.string,
  properties: t.union([
    t.record(t.string, t.union([ClassPropertiesV, t.null, t.undefined])),
    t.null,
    t.undefined
  ]),
  locked: t.boolean
});

export const OxySelectorV = t.type({
  id: t.string,
  name: t.string,
  properties: t.union([
    t.record(t.string, t.union([ClassPropertiesV, t.null, t.undefined])),
    t.null,
    t.undefined
  ]),
  children: t.union([t.array(OxyNestedSelectorV), t.null, t.undefined]),
  type: t.union([t.literal("class"), t.literal("custom")]),
  locked: t.boolean,
  collection: t.string
});

export type OxySelector = t.TypeOf<typeof OxySelectorV>;
export type OxyNestedSelector = t.TypeOf<typeof OxyNestedSelectorV>;

export const LoadedOxySelectorDataV = t.exact(
  t.type({
    template: t.string,
    selectors: t.array(OxySelectorV),
    collections: t.array(t.string),
    propertyPathsToWhitelistInFlatProps: t.array(t.string)
  })
);

export type LoadedOxySelectorData = t.TypeOf<typeof LoadedOxySelectorDataV>;

/*
 * Controls
 */
export type ControlOption =
  | {
      value: unknown;
      label: string;
      shortLabel?: string;
      icon?: IconName;
      iconHtml?: string;
      hideLabel?: boolean;
      group?: string;
    }
  | string;

export type ControlTypes =
  | "text"
  | "select"
  | "number"
  | "color"
  | "font-family"
  | "spacing"
  | "unit"
  | "button"
  | "button-bar"
  | "repeater"
  | "accordion"
  | "group"
  | "gap"
  | "align"
  | "code"
  | "border"
  | "media"
  | "media-size"
  | "border-radius"
  | "focus-point"
  | "video"
  | "variables";

export type BaseControl = {
  slug: string;
  name: string;
  type: ControlTypes;
  placeholder?: unknown;
  forcePlaceholder?: boolean;
  width?: number;
  items?: ControlOption[];
  hideLabel?: Boolean;
  disableVariable?: Boolean;
  positionLabel?: "left" | "bottom" | "top";
  disabledConditions?: Condition | Condition[] | (() => boolean);
  conditions?: Condition | Condition[] | (() => boolean);
};

export type ControlAccordion = BaseControl & {
  type: "accordion";
  open?: boolean;
  controls?: Control[];
  hideAfter: number;
};

export type ControlGroup = BaseControl & {
  type: "group";
  columns?: number;
  controls?: Control[];
};

export type ControlUnit = BaseControl &
  UnitOptions & {
    type: "unit";
    min?: number;
    max?: number;
    step?: number;
  };

export type ControlNumber = BaseControl & {
  type: "number";
  min?: number;
  max?: number;
  step?: number;
  arrows?: boolean;
};

export type ControlRepeater<RowType = Record<string, unknown>> = BaseControl & {
  type: "repeater";
  titleTemplate?: string | ((row: RowType | undefined) => string);
  defaultTitle?: string;
  buttonName?: string;
  defaultNewValue?: unknown;
  duplicateNewValue?: unknown;
  deleteText?: string;
  noDuplicate?: boolean;
  noDisable?: boolean;
  noDataMessage?: string;
  noExpand?: boolean;
  deleteConfirm?: boolean;
  itemIcon?: IconName;
  itemColorPath?: string;
  controls: Control[];
};

export type ControlButtonBar = BaseControl & {
  type: "button-bar";
  hideAfter: number;
};

export type ControlColor = BaseControl & {
  inline?: boolean;
  colorType?: ColorPickerType;
};

export type ControlImageSize = BaseControl & {
  imagePath?: string;
};

export type ControlButton = BaseControl & {
  type: "button";
  icon?: IconName;
  hideButtonLabel?: boolean;
  trueValue?: unknown;
  falseValue?: unknown;
};

export type ControlCode = BaseControl & {
  language: CodeInputLanguage;
  autofillOnEmpty?: string;
};

export type Control =
  | BaseControl
  | ControlAccordion
  | ControlGroup
  | ControlRepeater<any>
  | ControlNumber
  | ControlUnit
  | ControlButtonBar
  | ControlColor
  | ControlImageSize
  | ControlButton
  | ControlCode;

// Conditions
export type Operator =
  | "=="
  | "!="
  | ">"
  | "<"
  | ">="
  | "<="
  | "in"
  | "not in"
  | "contains"
  | "not contains"
  | "starts with"
  | "ends with";

export type Condition = [string, Operator, any];

// Repeater Rows

export type TransitionRow = {
  property: string;
  duration?: UnitInputValue;
  easing: string;
};

export type TextShadowRow = {
  x: UnitInputValue;
  y: UnitInputValue;
  blur: UnitInputValue;
  color: string;
};

export type BoxShadowRow = {
  position: "inset" | "outset";
  x: UnitInputValue;
  y: UnitInputValue;
  blur: UnitInputValue;
  spread: UnitInputValue;
  color: string;
};

export type FilterRow = {
  type:
    | "blur"
    | "drop-shadow"
    | "brightness"
    | "contrast"
    | "grayscale"
    | "hue-rotate"
    | "invert"
    | "saturate"
    | "sepia";
  value?: UnitInputValue;
  blur_value?: UnitInputValue;
  hue_value?: UnitInputValue;
  // drop-shadow
  x?: UnitInputValue;
  y?: UnitInputValue;
  blur?: UnitInputValue;
};

export type TransformRow = {
  type: "rotate" | "scale" | "skew" | "translate";
  translate_x?: UnitInputValue;
  translate_y?: UnitInputValue;
  translate_z?: UnitInputValue;
  scale_x?: number;
  scale_y?: number;
  scale_z?: number;
  rotate_x?: UnitInputValue;
  rotate_y?: UnitInputValue;
  rotate_z?: UnitInputValue;
  skew_x?: UnitInputValue;
  skew_y?: UnitInputValue;
};

export type OxygenControlsTreeContext = {
  getInheritanceMap: () => OxygenInheritance | undefined;
  getControlInheritance: (
    controlPath: string
  ) => OxygenInheritanceValue | undefined;
  getControlInheritanceByPrefix: (
    controlPath: string
  ) => OxygenInheritance | undefined;
  getParentControlInheritance: (
    controlPath: string
  ) => OxygenInheritance | undefined;
  getControlValue: (controlPath: string) => unknown;
  getControlValueAt: (controlPath: string, breakpoint: string) => unknown;
  setControlValue: (
    controlPath: string,
    value: Record<string, unknown>,
    transactionKey?: string
  ) => void;
  setControlValueAt: (
    controlPath: string,
    breakpoint: string,
    value: unknown,
    transactionKey?: string
  ) => void;
  getParentPath: (controlPath: string) => string;
  getParentControlValue: (controlPath: string) => unknown;
  getParentControlValueAt: (controlPath: string, breakpoint: string) => unknown;
};

export enum InheritancePosition {
  BEFORE = -1,
  CURRENT = 0,
  AFTER = 1
}

export type OxygenInheritanceValue = {
  value: unknown;
  position: InheritancePosition;
  breakpoint: string;
  from: string; // Which selector this values originates
  fromBreakpoint: boolean;
};

export type OxygenInheritance = Record<string, OxygenInheritanceValue>;
